import React, { useEffect, useState } from 'react';
import {
  Divider,
  Menu,
  MenuItem,
  Typography,
  IconButton,
  Link,
  Tooltip,
  TextField,
} from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EqualizerOutlinedIcon from '@mui/icons-material/EqualizerOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { formatCurrency } from 'utils/formatter';
import DataTable from 'components/DataTable';

const LOCALE = 'USD';
const InvoiceTable = (props) => {
  const {
    isOTR,
    invoices,
    page,
    pageSize,
    rowCount,
    fnViewOrderHandler,
    fnExpenseHandler,
    fnViewRemittanceHandler,
    fnDocumentUploadHandler,
    fnOnRowSelection,
    fnOnPageChange,
    fnOnPageSizeChange,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null); //Single invoice. usually through menu

  useEffect(() => {
    // // Notify parent about the selected rows
    fnOnRowSelection(selectedRows);
  }, [selectedRows]);

  // Handle selection change in DataGrid and notify parent
  const handleSelectedRowChange = (rows) => {
    const invoices_with_custom_amount = invoices.map(inv =>
      rows.includes(inv.id)
        ? { ...inv, customAmount: (inv.invoiceTotal - inv.paymentReceived) } // Add new property to the object
        : inv
    );
    const filterRows = invoices_with_custom_amount.filter(inv => rows.includes(inv.id));
    setSelectedRows(filterRows);
  };

  const toggleActionMenu = (evt, invoice) => {
    setSelectedInvoice(invoice);
    const target = evt ? evt.currentTarget : null;
    setAnchorEl(target);
  };

  const renderCustomAmount = (rowId) => {
    const filtered = selectedRows.filter(inv => inv.id === rowId);
    if (filtered && filtered.length > 0) {
      return filtered[0].customAmount;
    }
    return 0;
  };

  const handleAmountReceivedChange = (evt, id) => {
    const userAmount = Number(evt.target.value);
    const updated = selectedRows.map(inv => inv.id === id ? { ...inv, customAmount: userAmount } : { ...inv });
    setSelectedRows(updated);
  };

  const renderMultipleLines = (data = '') =>
    data.split(',').map(item => (
      <Typography key={item} variant='body2' sx={{ textDecoration: 'underline dotted', display: 'block' }}>
        {item}
      </Typography>
    ));

  const tableHeader = [
    { field: 'orderId', label: 'Load Id', numeric: false, disablePadding: true, visible: true },
    { field: 'broker', label: 'Broker', numeric: false, disablePadding: true, visible: true },
    { field: 'brokerReferenceNumber', label: 'Broker Load', numeric: false, disablePadding: true, visible: true },
    isOTR ?
      { field: 'truckNumber', label: 'Truck', numeric: false, disablePadding: true, visible: false } :
      { field: 'containerNumber', label: 'Container', numeric: false, disablePadding: true, visible: true },
    { field: 'driver', label: 'Driver', numeric: false, disablePadding: true, visible: true },
    { field: 'origins', label: 'Origin', numeric: false, disablePadding: true, visible: true },
    { field: 'destinations', label: 'Destination', numeric: false, disablePadding: true, visible: true },
    { field: 'invoiceTotal', label: 'Total', numeric: false, disablePadding: true, visible: true },
    { field: 'paymentReceived', label: 'Received', numeric: false, disablePadding: true, visible: true },
    { field: 'completeDate', label: 'Order Complete Date', numeric: false, disablePadding: true, visible: false },
    { field: 'invoiceDate', label: 'Invoice Date', numeric: false, disablePadding: true, visible: true },
    { field: 'generationDate', label: 'Generate Date', numeric: false, disablePadding: true, visible: true },
    { field: 'paymentReceivedDate', label: 'Payment Date', numeric: false, disablePadding: true, visible: true },
    { field: 'action', label: 'Action', numeric: false, disablePadding: true, visible: true },
  ];

  const createCustomInvoiceData = (invoice) => {
    const {
      id,
      orderId,
      displayOrderId,
      brokerName,
      brokerReferenceNumber,
      truckNumber,
      trailerNumber,
      containerNumber,
      driverName,
      origins,
      destinations,
      invoiceTotal,
      paymentReceived,
      paymentStatus,
      completeDate,
      invoiceDate,
      generationDate,
      paymentReceivedDate
    } = invoice;
    const row = {
      raw: {
        id: id,
        orderId: displayOrderId,
        broker: brokerName,
        brokerReferenceNumber: brokerReferenceNumber,
        origins: origins.split(',').map(o => o),
        destinations: destinations.split(',').map(d => d),
        trailerNumber: trailerNumber,
        containerNumber: containerNumber,
        driver: driverName,
        invoiceTotal: formatCurrency(invoiceTotal, 'en-US'),
        paymentReceived: formatCurrency(paymentReceived, 'en-US'),
        orderCompleteDate: completeDate,
        invoiceDate: invoiceDate,
        generationDate: generationDate,
        paymentReceivedDate: paymentReceivedDate,
      },
      render: {
        orderId: <Link color='primary' onClick={() => fnViewOrderHandler(orderId)} ><Typography variant='body2'>{displayOrderId}</Typography></Link>,
        broker: <Typography variant='body2'>{brokerName}</Typography>,
        brokerReferenceNumber: <Typography variant='body2'>{brokerReferenceNumber}</Typography>,
        origins: origins.split(',').length > 1 ? renderMultipleLines(origins) : <Typography variant='body2'>{origins}</Typography>,
        destinations: destinations.split(',').length > 1 ? renderMultipleLines(destinations) : <Typography variant='body2'>{destinations}</Typography>,
        trailerNumber: <Tooltip title={
          <Typography variant='body2'>{`Trailer Number: ${trailerNumber}`}</Typography>} arrow>
          <div style={{ textDecoration: 'underline dotted' }}>{truckNumber}</div>
        </Tooltip>,
        containerNumber: <Typography variant='body2'>{containerNumber}</Typography>,
        driver: <Typography variant='body2'>{driverName}</Typography>,
        invoiceTotal: <Typography variant='body2'>{formatCurrency(invoiceTotal, 'en-US')}</Typography>,
        paymentReceived: paymentStatus === 'RECEIVED' ? <Typography variant='body2'>{formatCurrency(paymentReceived, 'en-US')}</Typography>
          : <TextField
            size='small'
            margin='dense'
            variant='outlined'
            type='number'
            value={renderCustomAmount(id)}
            disabled={selectedRows.every(row => row.id !== id)}
            onChange={(evt) => handleAmountReceivedChange(evt, id)}
            sx={{ maxWidth: 85 }}
          />,
        orderCompleteDate: <Typography variant='body2'>{completeDate}</Typography>,
        invoiceDate: <Typography variant='body2'>{invoiceDate}</Typography>,
        generationDate: <Typography variant='body2'>{generationDate}</Typography>,
        paymentReceivedDate: <Typography variant='body2'>{paymentReceivedDate}</Typography>,
        action: <>
          <IconButton
            aria-label='settings'
            onClick={(evt) => toggleActionMenu(evt, invoice)}
            size='small'>
            <MoreVertIcon />
          </IconButton>
        </>
      }
    };
    return row;
  };

  const tableData = invoices.map(inv => createCustomInvoiceData(inv));

  return (
    <>
      <DataTable
        header={tableHeader.filter(cell => cell.visible)}
        rows={tableData}
        sortKey={'invoiceDate'}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        handlePageChange={fnOnPageChange}
        handlePageSizeChange={fnOnPageSizeChange}
        handleCheckboxToggle={selectedRows => handleSelectedRowChange(selectedRows)}
      />
      <Menu
        id='long-menu'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        keepMounted
        onClose={() => toggleActionMenu(null, null)}
      >
        <MenuItem
          key={`view:${selectedInvoice?.id}`}
          onClick={() => fnViewOrderHandler(selectedInvoice?.orderId)}
        >
          <VisibilityOutlinedIcon fontSize='small' />
          &nbsp;&nbsp;View Order
        </MenuItem>
        <Divider />
        <MenuItem
          key={`stats:${selectedInvoice?.id}`}
          onClick={() => fnViewRemittanceHandler(selectedInvoice)}
        >
          <EqualizerOutlinedIcon fontSize='small' />
          &nbsp;&nbsp;View Remittance
        </MenuItem>
        <Divider />
        <MenuItem
          key={`files:${selectedInvoice?.id}`}
          onClick={() => fnDocumentUploadHandler(selectedInvoice)}
        >
          <AttachFileIcon fontSize='small' />
          &nbsp;&nbsp;Upload Documents
        </MenuItem>
      </Menu>
    </>
  );
};

export default InvoiceTable;
